// Dialog zum Anlegen/Bearbeiten eines Zentral-Benutzers (DCP-2228 Folge-Feature)
//
// Die Ankreuzfelder kommen aus dem Katalog des Backends, nicht aus einer Liste hier:
// sonst driftet die UI von den Aktionen ab, die der Server überhaupt akzeptiert, und
// ein Häkchen würde stillschweigend nichts bewirken.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ManagedRole, ManagedUser, PermissionCatalog } from '@/types';

interface Props {
  open: boolean;
  /** null = anlegen; sonst bearbeiten (E-Mail dann gesperrt, sie ist der Schlüssel). */
  user: ManagedUser | null;
  catalog: PermissionCatalog;
  /** E-Mail des angemeldeten Admins — verhindert die Selbst-Degradierung im UI. */
  currentUserEmail: string;
  onOpenChange: (open: boolean) => void;
  onSave: (user: ManagedUser) => void;
  isSaving?: boolean;
}

const emptyUser = (): ManagedUser => ({
  email: '',
  role: 'zentral',
  permissions: {},
  canSwitchRole: false,
});

export function UserFormDialog({
  open,
  user,
  catalog,
  currentUserEmail,
  onOpenChange,
  onSave,
  isSaving,
}: Props) {
  const { t } = useTranslation('users');
  const [form, setForm] = useState<ManagedUser>(emptyUser);
  const [error, setError] = useState<string | null>(null);

  const isEdit = user !== null;
  const isDepotRole = form.role === 'partner' || form.role === 'mitarbeiter';
  const isSelf = isEdit && user.email.toLowerCase() === currentUserEmail.toLowerCase();

  useEffect(() => {
    if (!open) return;
    setForm(user ? { ...user, permissions: { ...user.permissions } } : emptyUser());
    setError(null);
  }, [open, user]);

  const toggleAction = (resource: string, action: string, checked: boolean) => {
    setForm((prev) => {
      const current = prev.permissions[resource] ?? [];
      const next = checked
        ? [...current, action]
        : current.filter((a) => a !== action);

      const permissions = { ...prev.permissions };
      // Leere Listen entfernen statt sie zu speichern: der Server behandelt einen
      // fehlenden Schlüssel und eine leere Liste gleich, und so bleibt die
      // gespeicherte Datei lesbar.
      if (next.length > 0) permissions[resource] = next;
      else delete permissions[resource];

      return { ...prev, permissions };
    });
  };

  const handleSave = () => {
    const email = form.email.trim().toLowerCase();

    if (!email.includes('@') || email.startsWith('@') || email.endsWith('@')) {
      setError(t('form.errors.email'));
      return;
    }

    onSave({ ...form, email });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('form.titleEdit') : t('form.titleCreate')}</DialogTitle>
          <DialogDescription>{t('form.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {t('form.fields.email')}
            </label>
            <Input
              type="email"
              value={form.email}
              // Die E-Mail ist der Schlüssel: sie zu ändern wäre ein neuer Benutzer
              // plus ein verwaister alter, nicht eine Umbenennung.
              disabled={isEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            />
            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              {t('form.fields.role')}
            </label>
            {/* Depot-Rollen sind hier nicht wählbar (Entscheidung 03.08.2026): sie
                funktionieren nur zusammen mit Depots und Vertragsfenster, und beide kommen
                aus commercetools. Eine per Hand erfundene Depot-Rolle wäre wirkungslos und
                sähe konfiguriert aus.

                ⚠️ Deshalb wird die Rolle bei einem BESTEHENDEN Depot-Eintrag nur angezeigt,
                nicht zur Auswahl gestellt. Sonst zeigte das Feld einen leeren Wert, und ein
                Speichern überschriebe die Rolle stillschweigend mit „zentral". */}
            {isDepotRole ? (
              <p className="text-sm">
                {t(`role.${form.role}` as never)}{' '}
                <span className="text-xs text-muted-foreground">
                  — {t('form.depotRoleLocked')}
                </span>
              </p>
            ) : (
              <Select
                value={form.role}
                disabled={isSelf}
                onValueChange={(v) => setForm((prev) => ({ ...prev, role: v as ManagedRole }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="zentral">{t('role.zentral')}</SelectItem>
                  <SelectItem value="admin">{t('role.admin')}</SelectItem>
                </SelectContent>
              </Select>
            )}
            {isSelf && <p className="text-xs text-muted-foreground">{t('form.selfRoleLocked')}</p>}
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              {t('form.fields.permissions')}
            </p>
            {Object.entries(catalog).map(([resource, actions]) => (
              <div key={resource} className="rounded-md border p-3">
                <p className="mb-2 text-sm font-medium">{t(`apps.${resource}`, resource)}</p>
                <div className="flex flex-wrap gap-3">
                  {actions.map((action) => (
                    <label key={action} className="flex items-center gap-1.5 text-xs">
                      <Checkbox
                        checked={form.permissions[resource]?.includes(action) ?? false}
                        onCheckedChange={(checked) =>
                          toggleAction(resource, action, checked === true)
                        }
                      />
                      {t(`actions.${action}`, action)}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">{t('form.fields.canSwitchRole')}</p>
              <p className="text-xs text-muted-foreground">{t('form.canSwitchRoleHint')}</p>
            </div>
            <Switch
              checked={form.canSwitchRole ?? false}
              onCheckedChange={(checked) =>
                setForm((prev) => ({ ...prev, canSwitchRole: checked }))
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('form.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('form.saving') : t('form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
